import { useRef, useState } from 'react';
import { formatUnits } from 'viem';

import { isTriggerOrderType } from '@/constants/advancedOrder';
import { CHAIN_IDS } from '@/constants/chain';

import { useAdvancedOrderType } from '@/hooks/useAdvancedOrderType';
import { useInvalidateTradeQuotes } from '@/hooks/useRefreshTrade';

import { expirationSelectors, useLimitOrderStore } from '@/store/limitOrder';
import { referrersStoreSelectors, useReferrerStore } from '@/store/referrals';
import {
  recipientAddressSelector,
  useMarketTradeSettingsStore,
} from '@/store/tradeSettings/market';

import { refreshOpenLimitOrdersUntilVisible } from '@/utils/0x/orderbook';
import type { TokenResult } from '@/utils/0x/token-registry.types';
import type { IntentsSubmitApproval } from '@/utils/intents/types';
import { isChainAddress } from '@/utils/models';

import {
  approveCrossChainSpend as approveCrossChainSpendAction,
  signIntentsApprovalAction,
  useApproveSellToken,
} from './executor/approvals';
import { selectLane } from './executor/selectLane';
import { executeRegisteredTrade } from './executor/strategies';
import type { ExecutionContext, TradeSubmitOutcome } from './executor/types';
import { useExecutionEnvironment } from './executor/useExecutionEnvironment';
import { executionLimitRateSelector, useLimitRateStore } from './limitRateStore';
import { executionInputSelector, useTradeModuleStore } from './store';
import { useTradeEngine } from './useTradeEngine';

export { useApproveSellToken };

/** Optional submission-local data produced by approval actions. */
export type SubmitTradeArgs = { signedApproval?: IntentsSubmitApproval };
type TradeExecutorArgs = {
  sellToken: TokenResult | undefined;
  buyToken: TokenResult | undefined;
};

/** Assembles rewrite-owned state and exposes the single trade submission action. */
export function useTradeExecutor({ sellToken, buyToken }: TradeExecutorArgs) {
  const { quote, tokenUsdPrices } = useTradeEngine();
  const chainId = sellToken?.chainId ?? CHAIN_IDS.MAINNET;

  const { environment, deps, writeContractAsync, queryClient } = useExecutionEnvironment(chainId);
  const { ethereumWallet, solanaWallet } = environment;
  const { recipientAddress } = useMarketTradeSettingsStore(recipientAddressSelector);
  const { referrerId, isExpired, clearReferrerId } = useReferrerStore(referrersStoreSelectors);
  const { expiration, customExpiration } = useLimitOrderStore(expirationSelectors);
  const { taggedLimitRate, resetLimitRate } = useLimitRateStore(executionLimitRateSelector);
  const [advancedOrderType] = useAdvancedOrderType();
  const { sellInput, buyInput, dispatchCheckout, resetInputs } =
    useTradeModuleStore(executionInputSelector);
  const invalidateTradeQuotes = useInvalidateTradeQuotes();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const isSubmittingRef = useRef(false);

  function buildContext(
    signedApproval: IntentsSubmitApproval | undefined,
  ): ExecutionContext | undefined {
    if (!sellToken || !buyToken) return undefined;
    const rawAccountAddress =
      sellToken.chainId === CHAIN_IDS.SOLANA ? solanaWallet?.address : ethereumWallet?.address;
    const accountAddress =
      rawAccountAddress && isChainAddress(rawAccountAddress) ? rawAccountAddress : undefined;
    let advanced: ExecutionContext['advanced'];
    if (quote.source === 'advanced') {
      if (quote.status !== 'derived' || !quote.buyAmount) return undefined;
      advanced = {
        expiration,
        customExpiration,
        taggedRate: taggedLimitRate,
        orderType: advancedOrderType,
        triggerCrossPrice: isTriggerOrderType(advancedOrderType)
          ? taggedLimitRate.rate.sell
          : undefined,
        buyAmountDecimal: formatUnits(BigInt(quote.buyAmount), buyToken.decimals),
      };
    }

    return {
      ...environment,
      quote,
      sellToken,
      buyToken,
      chainId: sellToken.chainId,
      accountAddress,
      sellInput,
      buyInput,
      tokenUsdPrices,
      recipientAddress,
      referrer: {
        id: referrerId,
        isExpired,
        clear: clearReferrerId,
      },
      signedApproval,
      advanced,
      deps: {
        ...deps,
        dispatchCheckout,
        resetInputs,
        resetLimitRate,
        invalidateTradeQuotes,
        refreshOpenLimitOrders: (maker, identity) => {
          void refreshOpenLimitOrdersUntilVisible(queryClient, maker, identity);
        },
      },
    };
  }

  async function submit({ signedApproval }: SubmitTradeArgs = {}): Promise<TradeSubmitOutcome> {
    if (isSubmittingRef.current) return { status: 'skipped' };

    const ctx = buildContext(signedApproval);
    if (!ctx || (quote.status !== 'derived' && (quote.status !== 'fetched' || !quote.raw))) {
      return { status: 'skipped' };
    }

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      const lane = selectLane(quote, ctx);
      return await executeRegisteredTrade(lane, ctx);
    } catch (error: unknown) {
      return {
        status: 'failed',
        error: error instanceof Error ? error : new Error(String(error)),
      };
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  async function signIntentsApproval() {
    const ctx = buildContext(undefined);
    return ctx ? signIntentsApprovalAction(ctx) : { status: 'aborted' as const };
  }

  async function approveCrossChainSpend() {
    if (isSubmittingRef.current) return { status: 'aborted' as const };
    const ctx = buildContext(undefined);
    if (!ctx) return { status: 'aborted' as const };

    isSubmittingRef.current = true;
    setIsSubmitting(true);
    try {
      return await approveCrossChainSpendAction(ctx, { writeContractAsync });
    } finally {
      isSubmittingRef.current = false;
      setIsSubmitting(false);
    }
  }

  return {
    submit,
    isSubmitting,
    signIntentsApproval,
    approveCrossChainSpend,
  };
}
